/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React, { useEffect, useState, useRef } from 'react';
import { View, Modal, Carousel, type CarouselInstanceRef, Icon, Divider } from '@az/starc-ui';
import { ChevronLeft, ChevronRight } from '@az/starc-ui-icons';
import styles from './styles.module.scss';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLabels } from '@/hooks/useLabels';
import { ModalThumbnails } from '../Thumbnails/ModalThumbnails';
import { type ReviewType, type ReviewMediaItem } from '../../../interface/types';
import { type ReviewPhotoMap } from '@/features/customReviews/hooks/useProductReviewImages';
import NextImage from '@/components/NextImage';

const labelMap = {
  imageAlt: 'label_reviews_imageAlt',
  videoAlt: 'label_reviews_videoAlt',
  previousImage: 'label_previous_image',
  nextImage: 'label_next_image',
};

type ReviewMediaDetailsModalProps = {
  isReviewDetailsModalOpen: boolean;
  handleReviewDetailsModalOpen: (stateValue: boolean) => void;
  mediaItems: ReviewMediaItem[];
  imageIndex: number;
  setImageIndex: (stateValue: number) => void;
  reviews: ReviewType[] | undefined | null | ReviewType;
  photoMap?: ReviewPhotoMap | null;
  renderReview?: (reviewData: ReviewType, detailsModal: boolean) => React.ReactNode;
};

const getCurrentReview = (
  media: ReviewMediaItem | undefined,
  reviews: ReviewType | ReviewType[] | null | undefined,
  photoMap?: ReviewPhotoMap | null,
): ReviewType | null | undefined => {
  if (!media || !reviews) {
    return undefined;
  }

  const mediaUrl = media.type === 'photo' ? media.url : media.thumbnailUrl;

  if (!photoMap || !Array.isArray(reviews)) {
    return Array.isArray(reviews) ? reviews[0] : reviews;
  }

  for (const reviewId in photoMap) {
    if (photoMap[reviewId]?.includes(mediaUrl)) {
      const foundReview = reviews.find((review) => review.reviewId === reviewId);
      return foundReview;
    }
  }

  return Array.isArray(reviews) ? reviews[0] : reviews;
};

const MediaContent = ({
  item,
  imageIndex,
  labels,
}: {
  item: ReviewMediaItem;
  imageIndex: number;
  labels: ReturnType<typeof useLabels<typeof labelMap>>;
}) =>
  item.type === 'photo' ? (
    <NextImage
      alt={item.caption || `${labels.imageAlt} ${imageIndex + 1}`}
      src={item.url}
      width={473}
      height={459}
      className={styles.modalSelectedImage}
    />
  ) : (
    <video
      controls
      controlsList="nodownload"
      className={styles.modalSelectedVideo}
      src={item.videoUrl}
      aria-label={item.caption || `${labels.videoAlt} ${imageIndex + 1}`}
      playsInline
    />
  );

export const ReviewMediaDetailsModal = ({
  isReviewDetailsModalOpen,
  handleReviewDetailsModalOpen,
  mediaItems,
  imageIndex,
  setImageIndex,
  reviews,
  photoMap,
  renderReview,
}: ReviewMediaDetailsModalProps) => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const labels = useLabels(labelMap);
  const containerRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<CarouselInstanceRef | null>(null);
  const [currentReview, setCurrentReview] = useState<ReviewType | undefined | null>(undefined);

  const isValidIndex = imageIndex >= 0 && imageIndex < mediaItems.length;

  useEffect(() => {
    if (isValidIndex) {
      const review = getCurrentReview(mediaItems[imageIndex], reviews, photoMap);
      setCurrentReview(review);
    }
  }, [imageIndex, mediaItems, photoMap, reviews, isValidIndex]);

  const handleNextImage = () => {
    if (imageIndex < mediaItems.length - 1) {
      carouselRef.current?.navigateForward();
      setImageIndex(imageIndex + 1);
    }
  };

  const handlePreviousImage = () => {
    if (imageIndex > 0) {
      carouselRef.current?.navigateBack();
      setImageIndex(imageIndex - 1);
    }
  };
  return (
    <Modal
      open={isReviewDetailsModalOpen}
      onClose={() => {
        handleReviewDetailsModalOpen(false);
        setImageIndex(0);
      }}
      className={styles.reviewImageModal}
      size={{ s: 'full', m: 'full', l: 'large', xl: 'large' }}
      hasCloseButton={true}
      headerColor={isDesktop ? 'secondary' : 'primary'}
    >
      <View className={styles.modalImagesSection} direction={'row'}>
        <View className={styles.modalSelectedImageContainer}>
          <View className={styles.modalSelectedImage} direction={'row'}>
            <Carousel controlRef={carouselRef} hideControls>
              <button
                onClick={handlePreviousImage}
                className={styles.chevron}
                aria-label={labels.previousImage}
                disabled={imageIndex === 0}
              >
                <Icon svg={ChevronLeft} size={6} />
              </button>

              {isValidIndex && (
                <MediaContent
                  item={mediaItems[imageIndex]}
                  imageIndex={imageIndex}
                  labels={labels}
                />
              )}

              <button
                onClick={handleNextImage}
                className={styles.chevron}
                aria-label={labels.nextImage}
                disabled={imageIndex === mediaItems.length - 1}
              >
                <Icon svg={ChevronRight} size={6} />
              </button>
            </Carousel>
          </View>
        </View>

        <View className={styles.imagesCarousel}>
          <ModalThumbnails
            mediaItems={mediaItems}
            imageIndex={imageIndex}
            setImageIndex={setImageIndex}
            containerRef={containerRef}
            data-testid="modal-thumbnails"
          />
        </View>
      </View>
      <Divider className={styles.customerInfoSeparator} />
      <View className={styles.reviewContent}>
        {renderReview && currentReview && renderReview(currentReview, true)}
      </View>
    </Modal>
  );
};
